import { useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { styled } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Typography from '@mui/material/Typography'
import Popover from '@mui/material/Popover'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ColorLensIcon from '@mui/icons-material/ColorLens'
import { DateTimePicker, TimeField } from '@mui/x-date-pickers-pro'
import DeleteIcon from '@mui/icons-material/Delete'
import { useTranslation } from 'react-i18next'
import { Controller, useFormContext } from 'react-hook-form'
import { SketchPicker } from 'react-color'
import { toast } from 'react-toastify'
import { format } from 'date-fns/format'

import {
  useSessionsStatusTypeOptions,
  useTagsDataOptions,
  useLocationDataOptions,
  useRoomsDataOptions,
  useEventOrganizerOptions,
  useSessionTypeOptions,
  getOption,
  formatDateTime,
  searchString,
} from '../../../../../utils'
import { useDeleteSessionMutation } from '../../../../../services/events'
import ContributorsAutocomplete from '../../../../root/components/ContributorsAutocomplete'
import type { ModifyEventSchema, SessionSchema } from '../../../../events/Events.schema'
import { StatusTag } from '../../../../root/components/StatusTag'

const Span = styled('span')<{ lineHeight?: number; bold?: boolean }>(
  ({ theme, lineHeight, bold }) => ({
    color: theme.palette.text.secondary,
    lineHeight: lineHeight ?? undefined,
    fontWeight: bold ? 'bold' : 'normal',
  })
)
const ColorPreview = styled('div')<{ value: string | null | undefined }>(({ value }) => ({
  minWidth: '1rem',
  minHeight: '1rem',
  backgroundColor: value ?? 'transparent',
  border: '0.1rem solid black',
  borderRadius: '50%',
}))

interface SessionFormProps {
  sessionKey: number
  location: number | undefined | null
  minDateTime: Date | undefined
  maxDateTime: Date | undefined
  onRemove: () => void
  onDuplicate?: (data: SessionSchema) => void
}

export default function SessionForm({
  sessionKey,
  location,
  minDateTime,
  maxDateTime,
  onRemove,
  onDuplicate,
}: SessionFormProps) {
  const { t } = useTranslation('events')
  const { eventId } = useParams()
  const statusOptions = useSessionsStatusTypeOptions(t)
  const locationsOptions = useLocationDataOptions()
  const roomsOptions = useRoomsDataOptions(location, locationsOptions)
  const tagsOptions = useTagsDataOptions()
  const sessionTypeOptions = useSessionTypeOptions(t)
  const eventOrganizerOptions = useEventOrganizerOptions(t)
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  const colorPickerRef = useRef<HTMLDivElement | null>(null)

  const [deleteSession] = useDeleteSessionMutation()

  const { control, watch, getValues } = useFormContext<ModifyEventSchema>()

  const name = watch(`sessions.${sessionKey}.name`)
  const start = watch(`sessions.${sessionKey}.start`)
  const end = watch(`sessions.${sessionKey}.end`)
  const id = watch(`sessions.${sessionKey}.id`)
  const color = watch(`sessions.${sessionKey}.color`) ?? '#D9D9D9'

  return (
    <Stack width="100%">
      <Typography
        variant="h5"
        margin={'0.5rem 0'}
        sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
      >
        {t('Séance')}
        <StatusTag
          color="#D9D9D9"
          size="small"
          position="top"
          absolute={false}
          sx={{ whiteSpace: 'nowrap' }}
        >
          ID {id}
        </StatusTag>
        <StatusTag color={color} size="small" position="top" absolute={false}>
          {name} - {formatDateTime(start)} - {formatDateTime(end)}
        </StatusTag>
      </Typography>

      <Stack
        spacing={1}
        padding={2}
        sx={theme => ({
          backgroundColor: theme.colors.greyBackground,
          borderRadius: '0 0 1rem 1rem',
          borderTop: `0.15rem solid ${theme.colors.secondary}`,
        })}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Span>{t('Détails de la séance')}</Span>
          <Stack direction="row" spacing={1}>
            <Controller
              control={control}
              name={`sessions.${sessionKey}.hide_from_generic_program`}
              render={({ field, fieldState }) => (
                <FormGroup>
                  <FormControlLabel
                    {...field}
                    control={<Switch defaultChecked={field.value} />}
                    label={t('Masquer du programme')}
                    sx={{ width: 'max-content' }}
                  />
                  {fieldState.error && (
                    <Typography color="error">{fieldState.error.message}</Typography>
                  )}
                </FormGroup>
              )}
            />
            {onDuplicate && (
              <IconButton
                onClick={event => {
                  event.preventDefault()
                  const data = getValues(`sessions.${sessionKey}`)
                  onDuplicate?.(data)
                }}
              >
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            )}
            <IconButton
              onClick={event => {
                event.preventDefault()
                if (
                  window.confirm(
                    t(
                      'Êtes-vous sûr de vouloir supprimer cette séance ? La suppression sera immédiate en base de données mais les interventions rattachées ne seront pas supprimées'
                    ) + '.'
                  )
                ) {
                  const id = getValues(`sessions.${sessionKey}.id`)
                  const isNew = getValues(`sessions.${sessionKey}._new`)
                  if (!isNew) {
                    deleteSession({ eventId: Number(eventId), id: id })
                      .then(data => {
                        if ('data' in data) {
                          toast.success(t('Séance supprimée'))
                        }
                        onRemove()
                      })
                      .catch(error => {
                        console.error(error)
                        toast.error(t('Une erreur est survenue'))
                      })
                  } else {
                    onRemove()
                    toast.success(t('Séance supprimée'))
                  }
                }
              }}
            >
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          </Stack>
        </Stack>
        <Controller
          control={control}
          name={`sessions.${sessionKey}.name`}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={t('Nom de la séance')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              fullWidth
              required
            />
          )}
        />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <Controller
            control={control}
            name={`sessions.${sessionKey}.short_name`}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={t('Alias')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
              />
            )}
          />
          <Controller
            control={control}
            name={`sessions.${sessionKey}.color`}
            render={({ field, fieldState }) => (
              <>
                <TextField
                  {...field}
                  label={t('Couleur')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  fullWidth
                  ref={colorPickerRef}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ColorPreview value={field.value} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setAnchorEl(colorPickerRef.current)}>
                          <ColorLensIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { paddingRight: '0.2rem' },
                  }}
                />
                <Popover
                  id={anchorEl ? 'simple-popover' : undefined}
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                >
                  <SketchPicker
                    color={field.value ?? ''}
                    onChange={color => {
                      field.onChange(color.hex ?? '')
                    }}
                    disableAlpha
                  />
                </Popover>
              </>
            )}
          />
        </Stack>
        <Controller
          control={control}
          name={`sessions.${sessionKey}.type`}
          render={({ field, fieldState }) => (
            <Autocomplete
              onChange={(_, value) => field.onChange(value?.value)}
              onBlur={field.onBlur}
              defaultValue={getOption(sessionTypeOptions, null, field.value)}
              options={sessionTypeOptions}
              id="type-autocomplete"
              disablePortal
              fullWidth
              renderInput={params => (
                <TextField
                  {...params}
                  disabled={field.disabled}
                  label={t('Type')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <Controller
            control={control}
            name={`sessions.${sessionKey}.start`}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <DateTimePicker
                label={t('Date de début')}
                name={field.name}
                minDateTime={minDateTime}
                maxDateTime={maxDateTime}
                defaultValue={field.value ? new Date(field.value) : null}
                onChange={date => {
                  field.onChange(date?.toISOString() ?? '')
                }}
                sx={{ flex: '1 0 auto' }}
                slotProps={{
                  textField: {
                    required: true,
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message,
                  },
                }}
              />
            )}
          />
          <Controller
            control={control}
            name={`sessions.${sessionKey}.end`}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <DateTimePicker
                label={t('Date de fin')}
                name={field.name}
                minDateTime={minDateTime}
                maxDateTime={maxDateTime}
                defaultValue={field.value ? new Date(field.value) : null}
                onChange={date => {
                  field.onChange(date?.toISOString() ?? '')
                }}
                format="HH:mm:ss"
                sx={{ flex: '1 0 auto' }}
                slotProps={{
                  textField: {
                    required: true,
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message,
                  },
                }}
              />
            )}
          />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <Controller
            control={control}
            name={`sessions.${sessionKey}.default_abstract_presentation_time`}
            render={({ field, fieldState }) => (
              <TimeField
                label={t('Durée de présentation')}
                name={field.name}
                defaultValue={field.value ? new Date(`2025-01-01T${field.value}`) : null}
                onChange={date => {
                  field.onChange(date ? format(date, 'HH:mm:ss') : '00:00:00')
                }}
                format="HH:mm:ss"
                sx={{ flex: '1 0 auto' }}
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message,
                  },
                }}
              />
            )}
          />
          <Controller
            control={control}
            name={`sessions.${sessionKey}.default_abstract_qa_time`}
            render={({ field, fieldState }) => (
              <TimeField
                label={t('Durée de questions')}
                name={field.name}
                defaultValue={field.value ? new Date(`2025-01-01T${field.value}`) : null}
                onChange={date => {
                  field.onChange(date ? format(date, 'HH:mm:ss') : '00:00:00')
                }}
                format="HH:mm:ss"
                sx={{ flex: '1 0 auto' }}
                slotProps={{
                  textField: {
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message,
                  },
                }}
              />
            )}
          />
        </Stack>
        <Controller
          control={control}
          name={`sessions.${sessionKey}.status`}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <Autocomplete
              onChange={(_, value) => field.onChange(value?.value ?? '')}
              onBlur={field.onBlur}
              defaultValue={getOption(statusOptions, null, field.value) ?? statusOptions[0]}
              options={statusOptions}
              id="status-autocomplete"
              disablePortal
              fullWidth
              disableClearable
              renderInput={params => (
                <TextField
                  {...params}
                  name={field.name}
                  disabled={field.disabled}
                  label={t('Statut')}
                  required
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name={`sessions.${sessionKey}.tags`}
          render={({ field, fieldState }) => (
            <Autocomplete
              onChange={(_, value) => {
                const parsedValue = value.reduce((acc, option) => {
                  if (option) acc.push(option.value)
                  return acc
                }, [] as number[])
                field.onChange(parsedValue ?? [])
              }}
              onBlur={field.onBlur}
              defaultValue={
                field.value ? field.value.map(tagId => getOption(tagsOptions, null, tagId)) : []
              }
              options={tagsOptions}
              multiple
              id="tags-autocomplete"
              disablePortal
              disableClearable
              fullWidth
              renderInput={params => (
                <TextField
                  {...params}
                  label={t('Tags')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name={`sessions.${sessionKey}.location`}
          render={({ field, fieldState }) => (
            <Autocomplete
              onChange={(_, value) => field.onChange(value?.value ?? null)}
              onBlur={field.onBlur}
              defaultValue={getOption(locationsOptions, null, field.value)}
              options={locationsOptions}
              id="location-autocomplete"
              disablePortal
              fullWidth
              renderInput={params => (
                <TextField
                  {...params}
                  name={field.name}
                  disabled={field.disabled}
                  label={t('Lieu')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name={`sessions.${sessionKey}.rooms`}
          render={({ field, fieldState }) => (
            <Autocomplete
              onChange={(_, value) => {
                const parsedValue = value.reduce((acc, option) => {
                  if (option) acc.push(option.value)
                  return acc
                }, [] as number[])
                field.onChange(parsedValue)
              }}
              onBlur={field.onBlur}
              defaultValue={field.value
                .map(id => getOption(roomsOptions, null, id))
                .filter(el => !!el)}
              options={roomsOptions}
              // Filter options when the user types. It should look for the rooms and the locations (label and group field)
              filterOptions={(options, state) =>
                options.filter(
                  option =>
                    searchString(option.label, state.inputValue) ||
                    searchString(option.group, state.inputValue)
                )
              }
              getOptionLabel={option =>
                option.groupId !== location ? `[${option.group}] ${option.label}` : option.label
              }
              id="rooms-autocomplete"
              groupBy={option => option.group}
              multiple
              disablePortal
              fullWidth
              renderInput={params => (
                <TextField
                  {...params}
                  label={t('Salle')}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name={`sessions.${sessionKey}.description`}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={t('Description')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              fullWidth
              multiline
              minRows={2}
            />
          )}
        />
        <Controller
          control={control}
          name={`sessions.${sessionKey}.comment`}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={t('Commentaire')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              fullWidth
              multiline
            />
          )}
        />
        <Controller
          control={control}
          name={`sessions.${sessionKey}.organizer`}
          render={({ field, fieldState }) => (
            <Autocomplete
              onChange={(_, value) => field.onChange(value?.value)}
              onBlur={field.onBlur}
              defaultValue={getOption(eventOrganizerOptions, null, field.value)}
              options={eventOrganizerOptions}
              id="organizer-autocomplete"
              disablePortal
              fullWidth
              renderInput={params => (
                <TextField
                  {...params}
                  disabled={field.disabled}
                  label={t('Organisateur')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          )}
        />
        <Controller
          control={control}
          name={`sessions.${sessionKey}.sponsor`}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={t('Sponsor')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              fullWidth
            />
          )}
        />
        <Controller
          control={control}
          name={`sessions.${sessionKey}.contributors`}
          render={({ field, fieldState }) => (
            <ContributorsAutocomplete
              prependName={field.name}
              defaultValue={field.value}
              onChange={field.onChange}
              label={<Span lineHeight={1}>{t('Contributeurs')}</Span>}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Stack>
    </Stack>
  )
}
